import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Building2, User, Phone, Mail, MapPin, Edit, Trash2 } from 'lucide-react';

export const ClientsPage = () => {
  const { api, user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [formData, setFormData] = useState({
    organization_name: '',
    contact_name: '',
    email: '',
    phone: '',
    address: ''
  });

  const fetchClients = async () => {
    try {
      const response = await api().get('/clients');
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [api]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedClient) {
        await api().put(`/clients/${selectedClient.id}`, formData);
        toast.success('Client updated successfully');
      } else {
        await api().post('/clients', formData);
        toast.success('Client created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save client');
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    try {
      await api().delete(`/clients/${clientId}`);
      toast.success('Client deleted successfully');
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete client');
    }
  };

  const openEditDialog = (client) => {
    setSelectedClient(client);
    setFormData({
      organization_name: client.organization_name,
      contact_name: client.contact_name,
      email: client.email,
      phone: client.phone,
      address: client.address
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setSelectedClient(null);
    setFormData({
      organization_name: '',
      contact_name: '',
      email: '',
      phone: '',
      address: ''
    });
  };

  const canEdit = user?.role === 'admin' || user?.role === 'assignor';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="clients-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase tracking-wide">Clients</h1>
          <p className="text-muted-foreground mt-1">Manage your school and organization clients</p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-client-btn" className="bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm">
                <Plus className="w-4 h-4 mr-2" />
                Add Client
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl uppercase">{selectedClient ? 'Edit Client' : 'New Client'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Organization Name</Label>
                  <Input
                    value={formData.organization_name}
                    onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                    placeholder="Denver High School"
                    required
                    data-testid="client-org-input"
                    className="bg-zinc-800 border-zinc-700 h-10 rounded-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Contact Name</Label>
                  <Input
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    placeholder="John Smith"
                    required
                    data-testid="client-contact-input"
                    className="bg-zinc-800 border-zinc-700 h-10 rounded-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="contact@school.edu"
                      required
                      data-testid="client-email-input"
                      className="bg-zinc-800 border-zinc-700 h-10 rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      required
                      data-testid="client-phone-input"
                      className="bg-zinc-800 border-zinc-700 h-10 rounded-sm"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 Main St, Denver, CO 80202"
                    required
                    data-testid="client-address-input"
                    className="bg-zinc-800 border-zinc-700 h-10 rounded-sm"
                  />
                </div>

                <Button type="submit" data-testid="client-submit-btn" className="w-full bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm h-10">
                  {selectedClient ? 'Update Client' : 'Create Client'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Clients Grid */}
      {clients.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-zinc-900/50 border border-zinc-800">
          <Building2 className="w-12 h-12 text-zinc-600 mb-4" />
          <p className="text-zinc-500 text-lg">No clients yet</p>
          {canEdit && <p className="text-zinc-600 text-sm mt-2">Click "Add Client" to add your first client</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map((client) => (
            <div 
              key={client.id}
              data-testid={`client-card-${client.id}`}
              className="bg-zinc-900/50 border border-zinc-800 p-5 hover:border-zinc-700 transition-colors"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="p-2 bg-purple-900/20 rounded-sm">
                  <Building2 className="w-5 h-5 text-purple-400" />
                </div>
                {canEdit && (
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEditDialog(client)}
                      data-testid={`edit-client-btn-${client.id}`}
                      className="text-zinc-400 hover:text-white h-8 w-8 p-0"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(client.id)}
                      data-testid={`delete-client-btn-${client.id}`}
                      className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>

              <h3 className="font-heading font-bold text-lg mb-3">{client.organization_name}</h3>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-zinc-400">
                  <User className="w-4 h-4 text-zinc-500" />
                  {client.contact_name}
                </div>
                <div className="flex items-center gap-2 text-zinc-400">
                  <Mail className="w-4 h-4 text-zinc-500" />
                  {client.email}
                </div>
                <div className="flex items-center gap-2 text-zinc-400">
                  <Phone className="w-4 h-4 text-zinc-500" />
                  {client.phone}
                </div>
                <div className="flex items-start gap-2 text-zinc-400">
                  <MapPin className="w-4 h-4 text-zinc-500 mt-0.5" />
                  <span className="line-clamp-2">{client.address}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
